import {
  Box,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { IoCreateSharp } from 'react-icons/io5'
import { MdOutlineDelete } from 'react-icons/md'

import {
  useDeleteQuestionOptionMutation,
  useUpdateQuestionOptionMutation,
} from '@/services/api'
import { QuestionOption } from '@/types'

import { ConfirmButton, Quill } from '..'

interface QuestionCardOptionsActionsProps {
  option: QuestionOption
  options: QuestionOption[]
  canEdit?: boolean
}

export function QuestionCardOptionsActions({
  option,
  options,
  canEdit = false,
}: QuestionCardOptionsActionsProps) {
  const [update, { isLoading: loadingUpdate, isSuccess: successUpdate }] =
    useUpdateQuestionOptionMutation()
  const [remove, { isLoading: loadingRemove, isSuccess: successRemove }] =
    useDeleteQuestionOptionMutation()
  const [response, setResponse] = useState<string>(option.option)
  const [isCorrect, setIsCorrect] = useState<boolean>(option.its_correct)
  const [feedback, setFeedback] = useState<string>(option.feedback || '')

  const handleUpdate = async () => {
    if (!response.trim() || response.trim() === '') {
      toast.error('Por favor, preencha o campo de texto para a opção!')
      return
    }

    if (
      options.some(({ id, its_correct }) => id !== option.id && its_correct) &&
      isCorrect
    ) {
      toast.error(
        'Não é possível adicionar duas alternativas como corretas. Você precisa remover a que está marcada ou adicionar uma nova como incorreta.',
      )
      return
    }

    await update({
      id: option.id,
      its_correct: isCorrect,
      option: response,
      order: option.order,
      feedback,
    })
  }

  const handleDelete = async () => {
    await remove(option.id)
  }

  return canEdit ? (
    <Box data-qa="question-card-options-actions" sx={{ display: 'flex', gap: 1 }}>
      <ConfirmButton
        variant="warning"
        title="Modificar alternativa"
        onConfirm={handleUpdate}
        loading={loadingUpdate}
        success={successUpdate}
        successMessage="A opção foi modificada com sucesso!"
        trigger={
          <IconButton color="primary" size="small" aria-label="edit">
            <IoCreateSharp />
          </IconButton>
        }
        body={
          <Stack spacing={2} my={2}>
            <Typography variant="body1">
              Escreva aqui o conteúdo da alternativa
            </Typography>
            <Quill value={response} onChange={(c) => setResponse(c)} />

            <Typography variant="body1">Esta é a resposta correta?</Typography>
            <RadioGroup
              value={isCorrect}
              onChange={(e) => setIsCorrect(e.target.value === 'true')}
              row>
              <FormControlLabel value={true} control={<Radio />} label="Sim" />
              <FormControlLabel value={false} control={<Radio />} label="Não" />
            </RadioGroup>

            <Typography>Feedback da questão</Typography>
            <Quill value={feedback} onChange={(c) => setFeedback(c)} />
          </Stack>
        }
      />
      <ConfirmButton
        variant="destructive"
        title={`Deseja realmente remover a opção ${option.order} da questão?`}
        body={<>Esta ação é irreversível.</>}
        onConfirm={handleDelete}
        loading={loadingRemove}
        success={successRemove}
        successMessage="A opção foi removida com sucesso!"
        trigger={
          <IconButton color="error" size="small" aria-label="delete">
            <MdOutlineDelete />
          </IconButton>
        }
      />
    </Box>
  ) : (
    <></>
  )
}
